import { Badge, Tooltip } from '@mantine/core';
import type { CoachStatus } from '../../types';

const MODALITY_DISPLAY_NAMES: Record<string, string> = {
  group_coaching: 'Group Coaching',
  one_on_one: '1:1 Coaching',
  one_on_one_coaching: '1:1 Coaching',
  workshop: 'Workshop',
};

const ACTIVE_STYLE: { bg: string; color: string; border?: string } = {
  bg: '#2D4739',
  color: 'white',
};

const INACTIVE_STYLE: { bg: string; color: string; border?: string } = {
  bg: '#F5F5F5',
  color: '#666666',
  border: '1px solid #E0E0E0',
};

const INACTIVE_TOOLTIP =
  'This coach is currently inactive. To reactivate, approve modalities in their profile under Learning & Development.';

export interface CoachStatusPillProps {
  status: CoachStatus;
  /** Approved modalities — used to compose the Active tooltip (AC6). */
  modalityApprovals?: string[] | null;
  /** When false, render bare badge (no Tooltip wrapper). Defaults to true. */
  showTooltip?: boolean;
}

function formatModalityList(approvals: string[]): string {
  return approvals.map(a => MODALITY_DISPLAY_NAMES[a] ?? a).join(', ');
}

export function buildCoachStatusTooltip(
  status: CoachStatus,
  modalityApprovals: string[] | null | undefined,
): string {
  if (status === 'inactive') return INACTIVE_TOOLTIP;
  const approvals = modalityApprovals ?? [];
  if (approvals.length === 0) {
    // Defensive: derived Active should imply at least one approval, but
    // keep a sensible fallback if a caller passes empty.
    return 'This coach is active and approved for at least one modality.';
  }
  return `This coach is active and approved for the following modalities: ${formatModalityList(approvals)}`;
}

export function CoachStatusPill({
  status,
  modalityApprovals,
  showTooltip = true,
}: CoachStatusPillProps) {
  const style = status === 'active' ? ACTIVE_STYLE : INACTIVE_STYLE;
  const label = status === 'active' ? 'Active' : 'Inactive';
  const badge = (
    <Badge
      tt="uppercase"
      size="xs"
      style={{
        backgroundColor: style.bg,
        color: style.color,
        border: style.border,
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '0.3px',
        cursor: 'default',
      }}
    >
      {label}
    </Badge>
  );

  if (!showTooltip) return badge;

  return (
    <Tooltip
      label={buildCoachStatusTooltip(status, modalityApprovals)}
      withArrow
      multiline
      w={280}
      position="top"
    >
      <span style={{ display: 'inline-block' }}>{badge}</span>
    </Tooltip>
  );
}
